import React, { useState } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { SeatManagement } from '@/components/settings/SeatManagement';

interface AccountTabProps {
  email: string;
  fullName: string;
  setFullName: React.Dispatch<React.SetStateAction<string>>;
}

export const ModernAccountTab = ({ email, fullName, setFullName }: AccountTabProps) => {
  const { toast } = useToast();
  const [isUpdating, setIsUpdating] = useState(false);
  
  const updateProfile = async () => {
    setIsUpdating(true);
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error('No user session found');
      }
      
      // Update profile in profiles table if it exists
      const { error } = await supabase
        .from('profiles')
        .upsert({
          id: session.user.id,
          full_name: fullName,
          updated_at: new Date().toISOString(),
        });
        
      if (error) throw error;
      
      toast({
        title: 'Profile Updated',
        description: 'Your profile has been successfully updated.',
      });
    } catch (error) {
      console.error('Error updating profile:', error);
      toast({
        title: 'Update Failed',
        description: 'There was an error updating your profile.',
        variant: 'destructive',
      });
    } finally {
      setIsUpdating(false);
    }
  };
  
  return (
    <div className="p-0">
      <CardHeader className="pb-3">
        <CardTitle className="text-slate-100">Account Settings</CardTitle>
        <CardDescription className="text-slate-400">
          Update your account information and manage your profile
        </CardDescription>
      </CardHeader>
      <CardContent className="p-6 pt-0">
        <div className="space-y-6">
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-medium mb-2 text-slate-200">Profile Information</h3>
              <p className="text-sm text-slate-400 mb-4">
                Your personal information used across Stewie Trader.
              </p>
            </div>
            
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="fullName" className="text-slate-300">Full Name</Label>
                <Input 
                  id="fullName" 
                  placeholder="Your Name"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className="bg-slate-600 border-slate-500 text-slate-50 placeholder-slate-400"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-300">Email Address</Label>
                <Input 
                  id="email" 
                  type="email" 
                  placeholder="your.email@example.com" 
                  value={email}
                  readOnly
                  className="bg-slate-600/50 border-slate-500/70 text-slate-200 cursor-not-allowed"
                />
                <p className="text-xs text-slate-500">
                  Contact support to change your email address
                </p>
              </div>
            </div>
          </div>
          
          <Separator className="bg-slate-700" />
          
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-medium mb-2 text-slate-200">Account Preferences</h3>
              <p className="text-sm text-slate-400 mb-4">
                Customize how you interact with the Stewie Trader platform.
              </p>
            </div>
            
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="timezone" className="text-slate-300">Timezone</Label>
                <select 
                  id="timezone"
                  className="w-full h-10 px-3 py-2 border border-slate-500 rounded-md bg-slate-600 text-sm text-slate-50"
                  defaultValue="America/New_York"
                >
                  <option value="America/New_York">Eastern Time (ET)</option>
                  <option value="America/Chicago">Central Time (CT)</option>
                  <option value="America/Denver">Mountain Time (MT)</option>
                  <option value="America/Los_Angeles">Pacific Time (PT)</option>
                  <option value="UTC">UTC</option>
                </select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="language" className="text-slate-300">Language</Label>
                <select 
                  id="language"
                  className="w-full h-10 px-3 py-2 border border-slate-500 rounded-md bg-slate-600 text-sm text-slate-50"
                  defaultValue="en"
                >
                  <option value="en">English</option>
                  <option value="es">Spanish</option>
                  <option value="fr">French</option>
                  <option value="de">German</option>
                </select>
              </div>
            </div>
          </div>
          
          <Separator className="bg-slate-700" />
          
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-medium mb-2 text-slate-200">Team Management</h3>
              <p className="text-sm text-slate-400 mb-4">
                Manage team members and their access to your trading platform.
              </p>
            </div>
            
            <div>
              <SeatManagement />
            </div>
          </div>
        </div>
      </CardContent>
      <CardFooter className="border-t bg-slate-800/50 border-slate-700 px-6 py-4">
        <Button 
          onClick={updateProfile} 
          disabled={isUpdating}
          className="bg-blue-600 hover:bg-blue-700 text-slate-50"
        >
          {isUpdating ? 'Saving Changes...' : 'Save Changes'}
        </Button>
      </CardFooter>
    </div>
  );
};